import type { DocumentListItem } from '@types';

/** IANA timezone all date keys derive from (F4.7 / F5.8). */
export const NASSAU_TZ = 'America/Nassau';

/**
 * Today's date in America/Nassau as an ISO `YYYY-MM-DD` string, independent of
 * the browser's local timezone. `en-CA` formats as `YYYY-MM-DD`.
 */
export function nassauToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: NASSAU_TZ }).format(now);
}

/**
 * EXPIRED indicator (F5.8), computed on read — never mutates status.
 * A quote is EXPIRED when it is ISSUED or ACCEPTED, has a `valid_until`, and
 * that date is strictly earlier than today in Nassau. Quotes WITHOUT
 * `valid_until` are never flagged; invoices are never flagged.
 */
export function isQuoteExpired(
  doc: Pick<DocumentListItem, 'type' | 'status' | 'valid_until'>,
  now: Date = new Date()
): boolean {
  if (doc.type !== 'quote') return false;
  if (doc.status !== 'ISSUED' && doc.status !== 'ACCEPTED') return false;
  if (!doc.valid_until) return false;
  // ISO `YYYY-MM-DD` strings compare correctly lexicographically.
  return doc.valid_until < nassauToday(now);
}

export type VatWarningLevel = 'none' | 'soft' | 'strong';

/** VAT-threshold banner level (F8.3). Soft > $80k, strong > $100k (BSD). */
export const VAT_SOFT_THRESHOLD = 80000;
export const VAT_STRONG_THRESHOLD = 100000;

/**
 * @param trailing12mRevenue decimal STRING from the API (server-computed, F8.2).
 * Compared, not summed — no client-side money arithmetic.
 */
export function vatWarningLevel(trailing12mRevenue: string): VatWarningLevel {
  const value = Number(trailing12mRevenue);
  if (!Number.isFinite(value)) return 'none';
  if (value > VAT_STRONG_THRESHOLD) return 'strong';
  if (value > VAT_SOFT_THRESHOLD) return 'soft';
  return 'none';
}

/**
 * Parse a decimal money STRING to an integer number of cents, WITHOUT floats.
 * Returns null for anything that is not a well-formed non-negative amount.
 * "1250" → 125000, "12.5" → 1250, "0.99" → 99.
 */
export function moneyToCents(value: string): number | null {
  const v = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  const [whole, frac = ''] = v.split('.');
  const cents = frac.padEnd(2, '0');
  return Number(whole) * 100 + Number(cents);
}

/** Format an integer number of cents back to a `"1234.56"` decimal string. */
export function centsToMoney(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const frac = String(abs % 100).padStart(2, '0');
  return `${sign}${whole}.${frac}`;
}

export interface PreviewTotals {
  subtotal: string;
  discount: string;
  total: string;
}

/**
 * Display-only ESTIMATE of the totals for the editor summary (F3.3). Computed in
 * integer cents (no float math) from the line-item amount strings and discount.
 * The authoritative totals ALWAYS come from the server response after save — this
 * is only a live preview and is labelled "estimate" in the UI. Malformed inputs
 * are skipped so a half-typed row never NaNs the estimate.
 */
export function previewTotals(
  lineItems: Array<{ amount: string }>,
  discount: string
): PreviewTotals {
  let subtotalCents = 0;
  for (const item of lineItems) {
    const c = moneyToCents(item.amount);
    if (c !== null) subtotalCents += c;
  }
  const discountCents = moneyToCents(discount) ?? 0;
  const totalCents = Math.max(0, subtotalCents - discountCents);
  return {
    subtotal: centsToMoney(subtotalCents),
    discount: centsToMoney(discountCents),
    total: centsToMoney(totalCents),
  };
}

/**
 * Format a decimal money STRING for display as `$X,XXX.XX` (F3.3). BSD is
 * pegged 1:1 to USD and both display a `$` symbol. This is display-only — the
 * value is never used for arithmetic (no parseFloat-for-math).
 */
export function formatMoney(value: string): string {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-BS', {
    style: 'currency',
    currency: 'BSD',
    currencyDisplay: 'narrowSymbol',
  }).format(safe);
}
